<script setup lang="ts">
    import { REQUEST_TYPES } from '@/constants/request-types';
    import { WIDGET_MODULES } from '@/constants/tracking';
    import formKitConfig from '@/formkit';
    import { extractRequestId } from '@/helpers/tracking';
    import { applyStartTimeFilterToProgramme } from '@/helpers/timeslots';
    import { type ProgrameeTyping, type SmallCardTyping, type SurveyItem } from '@/typings/widget';
    import { type FormKitNode } from '@formkit/core';
    import { FormKitProvider, FormKitRoot, defaultConfig } from '@formkit/vue';
    import Email from '@shared/components/form/email.vue';
    import PhoneNumber from '@shared/components/form/phone-number.vue';
    import TextInput from '@shared/components/form/text-input.vue';
    import { ConstantsDateTime } from '@shared/constants';
    import { DateTime } from '@shared/helpers';
    import { buildNewNotes } from '@shared/helpers/notes';
    import { type IdTyping } from '@shared/typings';
    import axios from 'axios';

    const props = defineProps<{
        venue: string
        locale: string
        eventDate: string
        eventTypeId: IdTyping
        productIds: IdTyping[]
        spaceIds: IdTyping[]
        programme: ProgrameeTyping[]
        timeframeId: number
        timeframes: SmallCardTyping[]
        surveyResponse: string
        surveyItems: SurveyItem[]
        guestAmount: number
        bookerName: string
    }>();

    const emit = defineEmits<{(event: 'next')}>();

    // Composables
    const { t } = useI18n();

    // Stores
    const { venueEventTypes, venueProducts, venueSpaces } = storeToRefs(useWidgetVenueStore());
    const { budgetAmount } = storeToRefs(useWidgetPathStore());
    const { startDateFilter, startTimeFilter } = storeToRefs(useWidgetFilterStore());
    const trackingStore = useWidgetTrackingStore();

    // Refs
    const formData: Ref<{
        name: string | null
        surname: string | null
        companyName: string | null
        email: string | null
        phoneCountryPrefix: string | null
        phoneNumber: string | null
    }> = ref({
        name: props.bookerName ?? '',
        surname: '',
        companyName: '',
        email: '',
        phoneCountryPrefix: '+31',
        phoneNumber: '',
    });

    // Computed
    const selectedTimeframe = computed(() => props.timeframes.find(timeframe => timeframe.id === props.timeframeId)?.title);
    const selectedSurvey = computed(() => props.surveyItems.find(item => item.value === props.surveyResponse)?.label);

    // Filter overrides: when a date/time filter is selected, it takes precedence over the
    // values gathered through the flow when building the final quote payload. Guest amounts
    // are intentionally NOT overridden here — they come from the user's input in
    // ProgramSelection (per-timeslot guest type amounts).
    const effectiveEventDate: ComputedRef<string> = computed(() => startDateFilter.value || props.eventDate);

    // Shift the first day's timeslots to start at the selected filter time before submitting.
    const effectiveProgramme: ComputedRef<ProgrameeTyping[]> = computed(() =>
        applyStartTimeFilterToProgramme(props.programme, startTimeFilter.value));

    // Methods
    const messageBuilder = (): string => {
        let template = '';

        if (selectedTimeframe.value) {
            const timeFrameText = t(selectedTimeframe.value);
            template += `- Timeframe: ${timeFrameText}\n\n`;
        }

        if (selectedSurvey.value) {
            const surveyText = t(selectedSurvey.value);
            template += `- Flexibility: ${surveyText}\n\n`;
        }

        return template;
    };

    // eslint-disable-next-line max-lines-per-function
    const buildProgram = (): ProgrameeTyping[] => {
        // eslint-disable-next-line max-lines-per-function
        const result = effectiveProgramme.value.map((day, dayIndex) => {
            const { timeslots, genericTimeslot } = day;

            const transformedGenericTimeslot = {
                name: genericTimeslot.name,
                notes: buildNewNotes(genericTimeslot.notes),
            };

            // eslint-disable-next-line max-lines-per-function
            const transformedTimeslots = timeslots.map(timeslot => {
                const { startDateTime, endDateTime, guestTypeAmounts, name, notes } = timeslot;

                const transformedGuestAmounts = guestTypeAmounts?.map(guestType => {
                    const { guestType: innerGuestType, amount } = guestType;

                    return {
                        guestTypeId: innerGuestType.id,
                        amount,
                    };
                });

                const oldStartDate = DateTime.dayjsObject(startDateTime);
                const oldEndDate = DateTime.dayjsObject(endDateTime);
                const newDate = DateTime.dayjsObject(effectiveEventDate.value)
                    .add(dayIndex, 'days');
                const businessDayDateStr = newDate.format('YYYY-MM-DD');

                // Apply business day rule (mirrors applyBusinessDayDate in VMA):
                // times 00:00–05:59 belong to the next calendar day
                // times 06:00–23:59 stay on the business day date
                const normalizeToBusinessDay = (dt: ReturnType<typeof DateTime.dayjsObject>): ReturnType<typeof DateTime.dayjsObject> => {
                    const calendarDate = dt.hour() < DateTime.BUSINESS_DAY_START_HOUR
                        ? newDate
                            .add(1, 'day')
                            .format('YYYY-MM-DD')
                        : businessDayDateStr;
                    return DateTime.dayjsObject(`${calendarDate} ${dt.format('HH:mm')}`);
                };

                const startDT = normalizeToBusinessDay(oldStartDate);
                let endDT = normalizeToBusinessDay(oldEndDate);

                // Fix midnight crossing: if end is before start after normalizing, add 1 day to end
                if (endDT.isBefore(startDT)) {
                    endDT = endDT.add(1, 'day');
                }

                const transformedStartDate = startDT.format(ConstantsDateTime.dateTimeFormatFormKit);
                const transformedEndDate = endDT.format(ConstantsDateTime.dateTimeFormatFormKit);

                return {
                    name,
                    notes: buildNewNotes(notes),
                    startDateTime: transformedStartDate,
                    endDateTime: transformedEndDate,
                    guestAmounts: transformedGuestAmounts,
                    products: [],
                    spaces: [],
                };
            });

            const transformedProducts = props.productIds
                .filter(productId => productId && productId !== 0)
                .map(productId => ({
                    productId,
                    amount: 1,
                    notes: buildNewNotes(venueProducts.value.find(product => product.id === productId)?.notes),
                }));

            const transformedSpaces = props.spaceIds
                .filter(spaceId => spaceId && spaceId !== 0)
                .map(spaceId => ({
                    spaceId,
                    amount: 1,
                    notes: buildNewNotes(venueSpaces.value.find(space => space.id === spaceId)?.notes),
                }));

            transformedTimeslots[0].products = transformedProducts as [];
            transformedTimeslots[0].spaces = transformedSpaces as [];

            return {
                name: `Day ${dayIndex + 1}`,
                date: DateTime.dayjsObject(effectiveEventDate.value)
                    .add(dayIndex, 'days')
                    .format(ConstantsDateTime.dateFormatCalendar),
                genericTimeslot: transformedGenericTimeslot,
                timeslots: transformedTimeslots,
            };
        });

        return result as unknown as ProgrameeTyping[];
    };
    // eslint-disable-next-line max-lines-per-function
    const processForm = async(submittedData: typeof formData.value, formNode: FormKitNode): Promise<void> => {
        const {
            name,
            surname,
            companyName,
            email,
            phoneNumber,
            phoneCountryPrefix,
        } = submittedData;

        const eventTypeName = venueEventTypes.value.find(type => type.id === props.eventTypeId)?.name ?? '';

        const payload = {
            eventStartDate: effectiveEventDate.value,
            event: {
                name: `Widget Custom Quote Request - ${eventTypeName}`,
                mainBooker: {
                    email,
                    userName: `${name} ${surname}`,
                    phoneCountryPrefix,
                    phoneNumber,
                    company: { name: companyName },
                },
                eventTypeId: Number(props.eventTypeId),
            },
            venuePackageId: '',
            requestType: REQUEST_TYPES.customQuote,
            guestAmount: Number(props.guestAmount),
            programme: buildProgram(),
            message: messageBuilder(),
        };

        try {
            const response = await axios
                .post(
                    `${import.meta.env.VITE_API_BASE_URL}/api/v1/public/quote/create`, payload, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': props.locale,
                            'X-Venue-Id': props.venue,
                        },
                    },
                );
            emit('next');

            /*
             * Analytics: fire only after the backend confirms (HTTP 2xx). This form is shared
             * between the standalone self-quote and the in-journey quote builder, so attribute to
             * whichever module is currently active.
             */
            trackingStore.trackRequestSubmitted({
                module: trackingStore.activeModule ?? WIDGET_MODULES.SELF_QUOTE,
                requestId: extractRequestId(response.data),
                eventType: eventTypeName || null,
                guestAmount: Number(props.guestAmount) || null,
                budget: budgetAmount.value || null,
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Posting request form failed: ', error);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const msg = t('widget.messages.errors.request-failed');
            formNode.setErrors([msg]);
        }
    };
</script>

<template>
    <div class="quote-form">
        <div class="quote-form__form">
            <FormKitProvider :config="defaultConfig(formKitConfig)">
                <FormKitRoot>
                    <FormKit
                        id="quote-form"
                        v-model="formData"
                        :incomplete-message="false"
                        :submit-label="t('widget.buttons.send-request')"
                        :submit-attrs="{
                            inputClass: 'btn btn--primary',
                            wrapperClass: 'im-on-the-wrapper',
                            outerClass: 'book-it-btn'
                        }"
                        type="form"
                        @submit="processForm"
                    >
                        <div class="quote-form__form__content">
                            <h2>
                                {{ t('widget.quote.form.title') }}
                            </h2>
                            <span>
                                {{ t('widget.quote.form.subtitle') }}
                            </span>
                            <div class="quote-form__form__content__row">
                                <TextInput
                                    id="name"
                                    :placeholder="`${t('widget.form.your-name')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-name') })}`
                                    }"
                                    name="name"
                                    validation="required"
                                />
                                <TextInput
                                    id="surname"
                                    :placeholder="`${t('widget.form.your-surname')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-surname') })}`
                                    }"
                                    name="surname"
                                    validation="required"
                                />
                            </div>
                            <TextInput
                                id="companyName"
                                :placeholder="t('widget.form.company-name')"
                                name="companyName"
                            />
                            <Email
                                id="email"
                                :placeholder="`${t('widget.form.email')}*`"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.email') })}`
                                }"
                                name="email"
                                validation="required"
                            />
                            <PhoneNumber
                                id="phoneNumber"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.phone-number') })}`
                                }"
                                name="phoneNumber"
                                prefix-name="phoneCountryPrefix"
                                validation="required"
                                placeholder="0 000 000 00*"
                            />
                        </div>
                    </FormKit>
                </FormKitRoot>
            </FormKitProvider>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.quote-form {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: var(--spacing-03);
    block-size: 100%;
    inline-size: 100%;

    @include min-width(md) {
        align-items: center;
        justify-content: center;
    }

    @include min-width(lg) {
        align-items: flex-start;
        block-size: auto;
    }

    &__form {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--color-pure-white);
        border-radius: var(--border-radius-md);
        background: var(--background-primary);
        gap: var(--spacing-03);
        padding-block: var(--spacing-05);
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            inline-size: 100%;
            margin-block-start: calc(-1 * 50px);
            margin-inline: auto;
            max-inline-size: 548px;
            padding-block: var(--spacing-06) var(--spacing-04);
            padding-inline: var(--spacing-09);
        }

        @include min-width(lg) {
            margin-block: 0 var(--spacing-05);
        }

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-02);

            @include min-width(md) {
                gap: var(--spacing-03);
            }

            h2, span {
                text-align: center;
            }

            span {
                display: block;
                margin-block-end: var(--spacing-04);
            }

            &__row {
                display: flex;
                justify-content: space-between;
                gap: var(--spacing-02);

                @include min-width(md) {
                    gap: var(--spacing-03);
                }

                > * {
                    flex: 1;
                }
            }
        }

        :deep(.formkit-actions) {
            display: flex;
            justify-content: center;
            padding-block-start: var(--spacing-03);

            @include min-width(md) {
                padding-block-start: var(--spacing-04);
                transform: none;
            }

            .book-it-btn {
                button {
                    font-size: var(--font-medium);
                    font-weight: var(--font-weight-medium);
                    min-inline-size: 174px;
                }
            }
        }
    }
}
</style>
